import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Lightbulb, ListChecks, Mail, FileText, Copy, Loader2, Trash2, Check, Clock } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import ProGate from "@/components/ProGate";

const TOOLS = [
  { id: "lesson_idea", label: "Lesson Ideas", icon: Lightbulb, hint: "e.g. A hands-on 5th grade science lesson about ecosystems", color: "#8FA9E3" },
  { id: "lesson_outline", label: "Lesson Outline", icon: ListChecks, hint: "e.g. 45-min lesson outline on the water cycle for 3rd grade", color: "#6FC2B0" },
  { id: "parent_email", label: "Parent Email", icon: Mail, hint: "e.g. Email to a parent about missing homework, warm & supportive tone", color: "#EE9C86" },
  { id: "teacher_note", label: "Note / Summary", icon: FileText, hint: "e.g. Summarize today's staff meeting into 5 bullet points", color: "#B69CE0" },
];

export default function AIDrafting() {
  const [tool, setTool] = useState("lesson_idea");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  const active = TOOLS.find((t) => t.id === tool);

  const loadHistory = async () => {
    try { const { data } = await api.get("/ai/drafts"); setHistory(data); } catch { /* */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Tell me what to draft");
    setLoading(true); setResult("");
    try {
      const { data } = await api.post("/ai/draft", { draft_type: tool, prompt });
      setResult(data.content);
      loadHistory();
      toast.success("Draft ready — review before you use it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Drafting failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };

  const deleteDraft = async (id) => {
    await api.delete(`/ai/drafts/${id}`);
    setHistory((p) => p.filter((d) => d.id !== id));
  };

  return (
    <ProGate title="Smart Drafting is a Pro feature" description="Draft lesson ideas, outlines, and parent emails in seconds with AI. Upgrade to unlock.">
    <div>
      <PageHeader
        title="Smart Drafting Studio" icon={Sparkles}
        subtitle="AI helps you draft. You always review and decide — nothing is ever sent for you."
      />

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Composer */}
        <div className="lg:col-span-3 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {TOOLS.map((t) => (
              <button key={t.id} onClick={() => { setTool(t.id); setResult(""); }} data-testid={`tool-${t.id}`}
                className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${tool === t.id ? "border-primary bg-primary/8" : "border-slate-100 bg-white hover:border-primary/40"}`}>
                <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-2" style={{ background: `${t.color}22` }}><t.icon size={18} style={{ color: t.color }} strokeWidth={1.7} /></div>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{t.label}</p>
              </button>
            ))}
          </div>

          <div className="tcc-card p-6 bg-gradient-to-br from-primary/8 via-white to-accent/12">
            <Label className="text-slate-600">What should I draft?</Label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt-input"
              placeholder={active.hint} rows={4} className="rounded-2xl mt-2 bg-white border-slate-200" />
            <Button onClick={generate} disabled={loading} data-testid="ai-generate-button" className="rounded-full bg-primary hover:bg-primary/90 mt-4 h-11 px-6">
              {loading ? <><Loader2 className="animate-spin mr-2" size={18} /> Drafting…</> : <><Sparkles size={17} className="mr-1.5" /> Generate draft</>}
            </Button>
          </div>

          {(result || loading) && (
            <div className="tcc-card p-6" data-testid="ai-result">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-slate-800">Your draft</h3>
                {result && <Button variant="outline" size="sm" onClick={() => copy(result)} className="rounded-full" data-testid="ai-copy-button">{copied ? <Check size={15} className="mr-1" /> : <Copy size={15} className="mr-1" />} Copy</Button>}
              </div>
              {loading ? (
                <div className="space-y-2 animate-pulse">
                  {[...Array(4)].map((_, i) => <div key={i} className="h-3 bg-slate-100 rounded-full" style={{ width: `${90 - i * 12}%` }} />)}
                </div>
              ) : (
                <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{result}</div>
              )}
            </div>
          )}
        </div>

        {/* History */}
        <div className="lg:col-span-2">
          <div className="tcc-card p-5">
            <div className="flex items-center gap-2 mb-4"><Clock size={17} className="text-primary" /><h3 className="font-heading font-bold text-slate-800">Recent drafts</h3></div>
            {history.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center">Your drafts will appear here.</p> : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {history.map((d) => {
                  const t = TOOLS.find((x) => x.id === d.draft_type);
                  return (
                    <div key={d.id} className="p-3 rounded-2xl bg-slate-50 group" data-testid="draft-history-item">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: t?.color }}>{t?.icon && <t.icon size={13} />} {t?.label}</span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          <button onClick={() => copy(d.content)} className="text-slate-400 hover:text-primary p-1"><Copy size={13} /></button>
                          <button onClick={() => deleteDraft(d.id)} className="text-slate-400 hover:text-destructive p-1"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-1 font-medium">{d.prompt}</p>
                      <p className="text-xs text-slate-500 line-clamp-3 mt-1">{d.content}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </ProGate>
  );
}
